import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tooltip } from '@mui/material';
import { PlaylistAddCheck, ErrorOutline } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetProjectsWithoutTasks } from '../redux/sagas/requests/project';

const ProjectList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const ProjectRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:last-of-type': {
        marginBottom: 0,
    },
    '&[data-interactive="true"]': {
        cursor: 'pointer',
    },
    '&[data-interactive="true"]:hover': {
        backgroundColor: theme.palette.surface.inset,
        borderColor: theme.palette.primary.main,
    },
}));

const RowHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const OverdueFlag = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.error.main,
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 1.5),
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const MetaItem = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
}));

const NoOwner = styled(MetaItem)(({ theme }) => ({
    color: theme.palette.warning.main,
    fontWeight: theme.typography.fontWeightMedium,
}));

const MoreRow = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(1),
    fontSize: theme.typography.size.sm,
    color: theme.palette.primary.main,
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(PlaylistAddCheck)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const ageLabel = (daysOld) => {
    if (daysOld === null || daysOld === undefined) return '';
    if (daysOld <= 0) return 'Created today';
    return daysOld === 1 ? 'Created yesterday' : `${daysOld} days old`;
};

const ProjectsWithoutTasksWidget = ({ widgetId, onRemove }) => {
    const navigate = useNavigate();

    const [extra, setExtra] = React.useState([]);
    const [loadingMore, setLoadingMore] = React.useState(false);

    const { data, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetProjectsWithoutTasks(),
        cacheKey: 'projects-without-tasks',
    });

    const items = React.useMemo(() => [...(data?.items || []), ...extra], [data, extra]);
    const total = data?.total || 0;
    const remaining = Math.max(0, total - items.length);
    const hasData = items.length > 0;

    const handleLoadMore = async () => {
        setLoadingMore(true);
        try {
            const response = await requestGetProjectsWithoutTasks(items.length);
            const seen = new Set(items.map((project) => project._id));
            setExtra((current) => [...current, ...(response?.data?.items || []).filter((project) => !seen.has(project._id))]);
        } catch (err) {
            setLoadingMore(false);
            return;
        }
        setLoadingMore(false);
    };

    const handleRowClick = (project) => {
        const slug = project.account?.slug;
        if (slug) navigate(`/account/${slug}/project/${project._id}`);
    };

    const renderOwner = (project) => {
        if (project.responsible?.name) return <MetaItem>{project.responsible.name}</MetaItem>;
        if (project.department?.name) return <MetaItem>{project.department.name} (no responsible)</MetaItem>;
        return <NoOwner>No owner yet</NoOwner>;
    };

    return (
        <BaseWidget
            title={`Projects Without Tasks${!isLoading && total > 0 ? ` (${total})` : ''}`}
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error ? 'Could not load this list — try refreshing.' : null}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        Nothing waiting to be broken down.
                    </Typography>
                </EmptyState>
            ) : (
                <ProjectList>
                    {items.map((project) => (
                        <ProjectRow
                            key={project._id}
                            data-interactive={project.account?.slug ? 'true' : 'false'}
                            role={project.account?.slug ? 'button' : undefined}
                            tabIndex={project.account?.slug ? 0 : undefined}
                            onClick={() => handleRowClick(project)}
                        >
                            <RowHeader>
                                <ProjectName>{project.name}</ProjectName>
                                {project.overdue && (
                                    <Tooltip title="Past its due date">
                                        <OverdueFlag>
                                            <ErrorOutline fontSize="small" />
                                            {project.daysOverdue} {project.daysOverdue === 1 ? 'day' : 'days'} overdue
                                        </OverdueFlag>
                                    </Tooltip>
                                )}
                            </RowHeader>
                            <MetaRow>
                                <MetaItem>{project.account?.name || project.client?.name || 'No account'}</MetaItem>
                                {renderOwner(project)}
                                <MetaItem>{ageLabel(project.daysOld)}</MetaItem>
                                {project.status && project.status !== 'Pending' && <MetaItem>{project.status}</MetaItem>}
                            </MetaRow>
                        </ProjectRow>
                    ))}
                    {remaining > 0 && (
                        <MoreRow onClick={handleLoadMore} role="button" tabIndex={0}>
                            {loadingMore ? 'Loading…' : `Show ${remaining} older`}
                        </MoreRow>
                    )}
                </ProjectList>
            )}
        </BaseWidget>
    );
};

export default ProjectsWithoutTasksWidget;
